import { ReactNode } from 'react';
import {
	Css,
	ICallToActionLink,
	Markdown,
	Responsive,
	StyledCallToAction,
} from '@/ui';
import styled from '@emotion/styled';
import { Color } from '../../../styles/Color';
import CallToActionLink from './CallToActionLink';
import { getHeadingStyle } from '../../../styles/utils/getHeadingStyle';
import { theme } from '../../../styles/theme';

const primaryBackground = Color.PrimaryBlue;
const primaryColor = Color.OnPrimarySurface;
const secondaryBackground = Color.SecondaryGreen2;
const secondaryColor = Color.OnSecondaryGreen2;

interface ICallToActions {
	ctaType?: 'grey' | 'blue' | 'green';
	title: string;
	body?: ReactNode | string;
	links?: ICallToActionLink[];
	hasDescription?: boolean;
	children?: ReactNode;
}

export default function CallToAction({
	ctaType = 'grey',
	title,
	body,
	links,
	hasDescription = true,
	children,
}: ICallToActions) {
	return (
		<StyledCallToActionWrap ctaType={ctaType} hasDescription={hasDescription}>
			<BodyWrap ctaType={ctaType}>
				<h3>{title}</h3>
				{children || <Markdown source={body as string} />}
			</BodyWrap>
			{links !== undefined && links.length > 0 && (
				<ButtonWrap>
					{links.map(
						(link, index) =>
							link && (
								<CallToActionLink
									key={link.key}
									link={link}
									ctaType={ctaType}
									secondary={link.secondary ?? index === 1}
								/>
							),
					)}
				</ButtonWrap>
			)}
		</StyledCallToActionWrap>
	);
}

const StyledCallToActionWrap = styled(StyledCallToAction)<{
	ctaType: ICallToActions['ctaType'];
}>`
	align-items: flex-end;
	background-color: ${({ ctaType }) => {
		if (ctaType === 'blue') return primaryBackground;
		if (ctaType === 'green') return secondaryBackground;
		return Color.Neutral94;
	}};

	h3 {
		${getHeadingStyle(theme, 'h5')}
		margin-bottom: ${Css.pxToRem(8)};
		color: ${({ ctaType }) => {
			if (ctaType === 'blue') return primaryColor;
			if (ctaType === 'green') return secondaryColor;
			return Color.PrimaryBlue;
		}};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		flex-direction: column;
		align-items: flex-start;
	}
`;

const BodyWrap = styled.div<{ ctaType: ICallToActions['ctaType'] }>`
	max-width: 50%;
	color: ${({ ctaType }) => {
		if (ctaType === 'blue') return Color.OnPrimarySurface;
		if (ctaType === 'green') return Color.OnSecondaryGreen2;
		return Color.OnBackgroundText;
	}};

	ul,
	p {
		margin: 0;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		margin-bottom: ${Css.pxToRem(24)};
		max-width: 100%;
	}
`;

const ButtonWrap = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-end;
	gap: ${Css.pxToRem(24)};

	a {
		text-decoration: none;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		flex-direction: column;
		align-items: flex-start;
		gap: ${Css.pxToRem(16)};
	}
`;
